import { CSSObject } from "@emotion/css/create-instance";
import ClassNames from "classnames";
import { FunctionComponent } from "react";
import { useAppSelector } from "../../store/hooks.js";
import buttonStyles from "../../stylesheets/components/button.module.css";
import styles from "../../stylesheets/components/image-overlay.module.css";
import {
  ColorScheme,
  OverlayModuleSettings,
  TextStyle,
  TranslatedModule,
} from "../../types/index.js";
import { css } from "../../utils/emotion.js";
import { getTranslatedButtonModules } from "../../utils/utils.js";
import MultilineText from "../MultilineText.js";
import ButtonModule from "./ButtonModule.js";

interface Props {
  translatedModule: TranslatedModule<OverlayModuleSettings>;
  pageId: string;
  scheme: ColorScheme;
  isPreview: boolean;
  isSlider: boolean;
}

const getStyles = (
  scheme: ColorScheme,
  textStyle: TextStyle,
  type: "primary" | "secondary",
): CSSObject => {
  const backgroundColor = scheme[type].background;

  const stylePlain: CSSObject = { color: scheme[type].text };
  const styleBackground: CSSObject = {
    ...stylePlain,
    boxShadow:
      `7px 3px 0 ${backgroundColor}, -7px 3px 0 ${backgroundColor}, ` +
      `7px 0 0 ${backgroundColor}, -7px 0 0 ${backgroundColor}`,
    backgroundColor: backgroundColor,
  };
  return textStyle === "background" ? styleBackground : stylePlain;
};

const OverlayModule: FunctionComponent<Props> = ({
  translatedModule: {
    id: moduleId,
    translation: {
      languageId,
      settings: { title, subtitle },
    },
    settings: { verticalAlign, horizontalAlign, textStyle, fontSize },
  },
  pageId,
  isPreview,
  scheme,
  isSlider,
}) => {
  const modules = useAppSelector((state) => state.modules);

  const buttons = getTranslatedButtonModules({
    modules,
    languageId,
    pageId,
    moduleId,
  });

  if (!title && !subtitle && buttons.length === 0) return null;

  return (
    <div
      className={ClassNames(
        styles.ImageOverlay,
        styles[`ImageOverlay--padding-${isSlider ? "large" : "small"}`],
        `FlexAlignH--${horizontalAlign}`,
        `FlexAlignV--${verticalAlign}`,
        {
          [styles["FlexAlignV--center"]]: verticalAlign === "center",
          [styles["FlexAlignV--top"]]: verticalAlign === "top",
          [styles["FlexAlignH--center"]]: horizontalAlign === "center",
        },
      )}
    >
      <div
        className={ClassNames(
          styles.ImageOverlay__Inner,
          styles[`ImageOverlay__Inner--size-${fontSize}`],
        )}
      >
        <div
          className={ClassNames(
            styles.ImageOverlay__Text,
            styles[`ImageOverlay__Text--${textStyle}`],
            {
              [styles["ImageOverlay__Text--offset-left"]]:
                textStyle === "background" && horizontalAlign === "left",
              [styles["ImageOverlay__Text--offset-right"]]:
                textStyle === "background" && horizontalAlign === "right",
            },
          )}
        >
          {title && (
            <div className={styles.ImageOverlay__Title}>
              <span
                className={ClassNames(
                  styles.ImageOverlay__TitleInner,
                  css(getStyles(scheme, textStyle, "primary")),
                )}
              >
                <MultilineText text={title} />
              </span>
            </div>
          )}
          {subtitle && (
            <div className={styles.ImageOverlay__Subtitle}>
              <span className={css(getStyles(scheme, textStyle, "secondary"))}>
                <MultilineText text={subtitle} />
              </span>
            </div>
          )}
        </div>
        {buttons.length > 0 && (
          <div
            className={ClassNames(
              buttonStyles.ButtonGroup,
              horizontalAlign !== "justify"
                ? buttonStyles[`ButtonGroup--align-${horizontalAlign}`]
                : undefined,
            )}
          >
            {buttons.map((button) => (
              <ButtonModule
                key={button.id}
                scheme={scheme}
                isPreview={isPreview}
                translatedModule={button}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OverlayModule;
